// Left hand program column: lock icon, product icon, exception and flag icons,
// stale badge, lock day re-qualify links, guidelines and profit margin.
import Grid from '@elliemae/ds-grid';
import { DSIconColors } from '@elliemae/ds-icon';
import { FlagSolid, WarningSquare } from '@elliemae/ds-icons';
import { NotificationBadge } from '@elliemae/ds-notification-badge';
import { DSTooltipV2 } from '@elliemae/ds-tooltip';
import { DSTypography, TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import { LoanProgram } from '@frontend/pricing-search';
import Rights from '../../../../common/services/Rights';
import { checkLockProgramStatus, getLockIconDisplay } from '../../helpers';
import {
  EXCEPTION_APPLIED_LABEL,
  isExceptionProgram,
} from '../EligibilityException';
import { getProgramGuidelines, removeSeconds } from './helpers';
import {
  StyledGuidelinesLink,
  StyledProgramIcons,
  StyledInlineLink,
  StyledNotificationBadge,
} from './styled';

// pui-theme has no equivalent token.
const STALE_BADGE_COLOR = '#fbde31';
const FLAG_COLOR = '#005181';

const PRODUCT_ICONS: Record<number, { src: string; alt: string }> = {
  2: { src: 'img/fha.gif', alt: 'FHA' },
  3: { src: '/img/va.gif', alt: 'VA' },
  4: { src: '/img/usda.gif', alt: 'USDA' },
};

/** wcpPolicyApplied is on the rates payload but not on the LoanProgram model. */
type ProgramRow = LoanProgram & { wcpPolicyApplied?: boolean };

export interface ProgramCellProps {
  program: ProgramRow;
  relockRequest: { specificProgramID?: number } | null;
  rateRequestStatus?: number;
  isHistoricalPricing: boolean;
  expanded: boolean;
  disableLockLinks: boolean;
  loanData: unknown;
  onRefreshLockDays: (lockDays: string, loanData: unknown) => void;
}

export const ProgramCell = ({
  program,
  relockRequest,
  rateRequestStatus,
  isHistoricalPricing,
  expanded,
  disableLockLinks,
  loanData,
  onRefreshLockDays,
}: ProgramCellProps) => {
  const {
    programID,
    program: programName,
    documentation,
    nonOptions,
    lockDays,
    locksAvailable,
    rateDate,
    holdback,
    activeGuidelineLink,
    productTypeID,
    wcpPolicyApplied,
    rateStale,
    rateDocMaxAge,
  } = program;

  // Show on the matched program in every lock state; getLockIconDisplay
  // renders locked / requested / cancelled by rateRequestStatus.
  const displayLockIcon = checkLockProgramStatus(relockRequest, programID);
  const {
    LockIcon,
    color: lockIconColor,
    title: lockIconTitle,
  } = getLockIconDisplay(rateRequestStatus);

  const productIcon = PRODUCT_ICONS[productTypeID as number];
  const showRateStaleBadge =
    rateStale === 1 && rateDocMaxAge !== -1 && !isHistoricalPricing;

  const openGuidelines = async () => {
    let credentials;
    try {
      credentials = await getProgramGuidelines(programID);
    } catch {
      // The form simply does not submit. Surfacing the failure to the user
      // needs a toast container on this page.
      return;
    }
    // The form is rendered by this component, but the lookup can still miss if
    // the block unmounts while the request is in flight.
    const form = document.getElementById(
      `allregs-${programID}`,
    ) as HTMLFormElement | null;
    if (!form) return;
    form.action = credentials.allRegsUrl;
    form.appId.value = credentials.applicationIdentifier;
    form.passkey.value = credentials.passKey;
    form.submit();
  };

  return (
    <Grid cols={['auto', '1fr']} gutter="xxs" alignItems="start">
      {displayLockIcon ? (
        <LockIcon color={lockIconColor} title={lockIconTitle} />
      ) : (
        <Grid width="16px" />
      )}

      <Grid rows={['auto', 'auto']} gutter="xxxs">
        <Grid cols={['1fr', 'auto']} alignItems="center">
          <DSTypography
            variant={TYPOGRAPHY_VARIANTS.B1}
            fontSize="13px"
            fontWeight={700}
            color="#353c46"
            data-testid="program-name"
          >
            {programName} - {documentation}
          </DSTypography>
          <StyledProgramIcons>
            {productIcon && <img src={productIcon.src} alt={productIcon.alt} />}
            {isExceptionProgram(program) && (
              <DSTooltipV2
                title={EXCEPTION_APPLIED_LABEL}
                placement="top"
                triggerComponent={
                  <WarningSquare size="s" color={DSIconColors.WARNING} />
                }
              />
            )}
            {nonOptions !== '' && <FlagSolid size="s" color={FLAG_COLOR} />}
          </StyledProgramIcons>
        </Grid>

        <Grid cols={['1fr', 'auto']} gutter="xs" alignItems="center">
          <DSTypography variant={TYPOGRAPHY_VARIANTS.B1}>
            {lockDays}-day rate{' '}
            {wcpPolicyApplied
              ? 'WCP Applied'
              : `as of ${removeSeconds(String(rateDate))}`}
            {showRateStaleBadge && (
              <StyledNotificationBadge>
                <NotificationBadge
                  value="Stale"
                  color={STALE_BADGE_COLOR}
                  size="s"
                  type="number"
                />
              </StyledNotificationBadge>
            )}
            {/* Show expanded lock options when the row is expanded */}
            {expanded && (
              <span>
                {' ( '}
                {locksAvailable
                  .split(',')
                  .map((lock) =>
                    lockDays !== Number(lock) ? (
                      <StyledInlineLink
                        key={lock}
                        disabled={disableLockLinks}
                        aria-label={`${lock} day lock`}
                        onClick={() => onRefreshLockDays(lock, loanData)}
                      >
                        {lock}
                      </StyledInlineLink>
                    ) : (
                      <strong key={lock}>{lock}</strong>
                    ),
                  )
                  .reduce((prev, curr) => [prev, ' ', curr] as never)}
                {' )'}
              </span>
            )}
            {activeGuidelineLink === 1 &&
              Rights.viewInvestorGuidelineOverride && (
                <>
                  <form
                    id={`allregs-${programID}`}
                    method="post"
                    target="_blank"
                    style={{ display: 'none' }}
                  >
                    <input type="hidden" name="appId" id="appId" />
                    <input type="hidden" name="passkey" id="passkey" />
                  </form>
                  <StyledGuidelinesLink
                    onClick={() => {
                      void openGuidelines();
                    }}
                  >
                    Guidelines
                  </StyledGuidelinesLink>
                </>
              )}
          </DSTypography>

          {Rights.viewProfitMargin && (
            <DSTypography
              variant={TYPOGRAPHY_VARIANTS.B1}
              fontSize="13px"
              fontWeight={600}
              color="#353c46"
            >
              Profit Margin: {holdback.toFixed(3)}
            </DSTypography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
